import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Brand} from '@vio/core/src/brand/types'
import {
  checkIsBrandVio,
  getLocalizedContactUrl,
  getLocalizedHelpUrl
} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {useDeviceLayout} from '../../../context/deviceLayout'
import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Divider} from '../Divider'
import {Text} from '../Text'
import {FooterLink} from './FooterLinkButton'
import {SocialMediaLinksWidget} from './SocialMediaLinksWidget'

const CENTER_DIVIDER_WIDTH = 136

type ForcedLayout = 'compact' | 'wide'

export interface Props {
  /** Brand configuration. It can be one of our brands or a white-label brand. Determines which links and information to display. */
  brand: Brand
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Which layout to force. If not set, the component will use the device layout to determine which layout to display. */
  forcedLayout?: ForcedLayout
  /** The component to render in the widget slot (e.g. Trustpilot) */
  widget?: React.ReactNode
  /** The component to render in the getTheApp slot */
  getTheAppSlot?: React.ReactNode
  /** The component to render in the cookie preference link slot */
  CookiePreferencesLinkSlot?: React.ReactNode
  /** The language code to use for the footer links */
  languageCode?: string
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${cssTheme.colors.background.neutral.c000};
`

export const FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${cssTheme.layout.spacing.s500};

  &.--compact {
    ${linariaMq.desktopXs} {
      margin: ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500}
        ${cssTheme.layout.spacing.s500};
    }
  }

  &.--wide {
    margin: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
  }
`

export const FooterCopyright = styled(Text)`
  white-space: nowrap;
`

export const FooterNav = styled.nav`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: ${cssTheme.layout.spacing.s500};
  color: ${cssTheme.colors.content.neutral.c950};
  ${cssTheme.typography.bodyXS};
`

export const FooterNavList = styled.ul<{layoutIsCompact: boolean}>`
  list-style-type: none;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({layoutIsCompact}) =>
    layoutIsCompact ? 'center' : 'flex-start'};
  align-items: center;
  width: 100%;
  padding: ${cssTheme.layout.spacing.s300} 0;
  > li + li {
    margin-left: ${cssTheme.layout.spacing.s500};
  }
`

export const FooterWidgetsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  gap: ${cssTheme.layout.spacing.s600};
  margin: ${cssTheme.layout.spacing.s250} 0;
`
export const CenterDividerWrapper = styled.div`
  width: ${CENTER_DIVIDER_WIDTH}px;
  margin-top: ${cssTheme.layout.spacing.s500};
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

export const Footer = ({
  brand,
  widget,
  className,
  forcedLayout,
  getTheAppSlot,
  CookiePreferencesLinkSlot,
  languageCode
}: Props) => {
  const {isDesktopMd, isDesktopLg, isDesktop} = useDeviceLayout()

  const layoutIsCompact =
    forcedLayout === undefined
      ? !(isDesktopMd || isDesktopLg)
      : forcedLayout === 'compact'

  const brandIsVio = checkIsBrandVio(brand)

  const shouldRenderWidgetsWrapper = !!widget || brandIsVio

  const currentYear = new Date().getFullYear()
  return (
    <Wrapper className={className}>
      <Divider />
      <FooterWrapper className={cx(layoutIsCompact ? '--compact' : '--wide')}>
        {shouldRenderWidgetsWrapper && (
          <FooterWidgetsWrapper>
            {widget}
            {brandIsVio && !layoutIsCompact && (
              <SocialMediaLinksWidget brand={brand} />
            )}
          </FooterWidgetsWrapper>
        )}
        {brandIsVio && getTheAppSlot && !isDesktop && getTheAppSlot}

        {widget && layoutIsCompact && (
          <CenterDividerWrapper>
            <Divider />
          </CenterDividerWrapper>
        )}

        <FooterNav>
          <FooterNavList layoutIsCompact={layoutIsCompact}>
            <li>
              <FooterCopyright variant="bodyXS">
                © {currentYear} {brand.name}
              </FooterCopyright>
            </li>
            {brand.links.aboutUsUrl && (
              <li>
                <FooterLink href={brand.links.aboutUsUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="AboutUs"
                    id="about"
                    defaultMessage="About"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.careersUrl && (
              <li>
                <FooterLink href={brand.links.careersUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="Careers"
                    id="careers"
                    defaultMessage="Careers"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.developersUrl && (
              <li>
                <FooterLink href={brand.links.developersUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="Affiliates"
                    id="affiliates"
                    defaultMessage="Affiliates"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.blogUrl && (
              <li>
                <FooterLink href={brand.links.blogUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="Blog"
                    id="blog"
                    defaultMessage="Blog"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.helpUrl && (
              <li>
                <FooterLink
                  href={getLocalizedHelpUrl(brand, languageCode)}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="Help"
                    id="help"
                    defaultMessage="Help"
                  />
                </FooterLink>
              </li>
            )}
            {Boolean(CookiePreferencesLinkSlot) && (
              <>{CookiePreferencesLinkSlot}</>
            )}
            {brand.links.privacyPolicyUrl && (
              <li>
                <FooterLink href={brand.links.privacyPolicyUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="PrivacyPolicy"
                    id="privacy"
                    defaultMessage="Privacy"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.termsOfUseUrl && (
              <li>
                <FooterLink href={brand.links.termsOfUseUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="TermsOfUse"
                    id="terms"
                    defaultMessage="Terms"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.contactUrl && (
              <li>
                <FooterLink
                  href={getLocalizedContactUrl(brand, languageCode)}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="Contact"
                    id="contact"
                    defaultMessage="Contact"
                  />
                </FooterLink>
              </li>
            )}
            {brandIsVio && isDesktop && getTheAppSlot && (
              <li>{getTheAppSlot}</li>
            )}
            {brandIsVio && layoutIsCompact && (
              <li>
                <SocialMediaLinksWidget brand={brand} />
              </li>
            )}
          </FooterNavList>
        </FooterNav>
      </FooterWrapper>
    </Wrapper>
  )
}
